import React, { useState, useEffect } from 'react';
import { Modal, Form, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import LinksButton from 'shared/design-system/components/links-button';
import { DettagliProgettoDtoStatoEnum } from '@api/client';

export interface RescheduleData {
  months: number;
  direction: 'forward' | 'backward';
  shiftExternalCosts: boolean;
  shiftStaff: boolean;
  shiftProgress: boolean;
}

interface ModalRescheduleProps {
  show: boolean;
  setShow: (show: boolean) => void;
  onConfirm: (data: RescheduleData) => void;
  projectStatus?: DettagliProgettoDtoStatoEnum;
  hasStaff?: boolean;
  hasExternalCosts?: boolean;
  canShiftProgress?: boolean;
}

const ModalReschedule: React.FC<ModalRescheduleProps> = ({
  show,
  setShow,
  onConfirm,
  projectStatus,
  hasStaff = false,
  hasExternalCosts = false,
  canShiftProgress = false,
}) => {
  const { t } = useTranslation();

  const [months, setMonths] = useState<number | null>(1);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [shiftExternalCosts, setShiftExternalCosts] = useState<boolean>(false);
  const [shiftStaff, setShiftStaff] = useState<boolean>(false);
  const [shiftProgress, setShiftProgress] = useState<boolean>(false);

  // L'unico controllo è sulla presenza di ricavi: ci sono ricavi, posso traslarli
  const isRevenueDisabled = !canShiftProgress;

  const isStaffDisabled = !hasStaff;
  const isExternalCostsDisabled = !hasExternalCosts;

  // Preseleziona tutti gli elementi disponibili quando il modal si apre
  useEffect(() => {
    if (show) {
      setShiftStaff(hasStaff);
      setShiftExternalCosts(hasExternalCosts);
      setShiftProgress(canShiftProgress);
    }
  }, [show, hasStaff, hasExternalCosts, canShiftProgress]);

  const handleConfirm = () => {
    if (months === null) return;
    onConfirm({
      months,
      direction,
      shiftExternalCosts,
      shiftStaff,
      shiftProgress: isRevenueDisabled ? false : shiftProgress,
    });
    // Non resettare immediatamente - il reset avverrà dopo il successo della chiamata
  };

  const handleClose = () => {
    setShow(false);
    resetForm();
  };

  const resetForm = () => {
    setMonths(1);
    setDirection('forward');
    setShiftExternalCosts(false);
    setShiftStaff(false);
    setShiftProgress(false);
  };

  const isFormValid = () => {
    const hasAtLeastOneCheckbox =
      (shiftStaff && !isStaffDisabled) ||
      (shiftExternalCosts && !isExternalCostsDisabled) ||
      (shiftProgress && !isRevenueDisabled);
    const hasAtLeastOneAvailableOption = hasStaff || hasExternalCosts || canShiftProgress;
    return months !== null && months > 0 && hasAtLeastOneCheckbox && hasAtLeastOneAvailableOption;
  };

  const renderTooltip = (props: any) => (
    <Tooltip id="reschedule-info-tooltip" {...props}>
      {t('project-detail.reschedule-info-tooltip')}
    </Tooltip>
  );

  const handleIncrement = () => {
    setMonths((prev) => (prev ? prev + 1 : 1));
  };

  const handleDecrement = () => {
    setMonths((prev) => (prev && prev > 1 ? prev - 1 : 1));
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.1em' }}>
          {t('project-detail.reschedule-modal-title')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 py-4">
        <Form>
          <Form.Group className="mb-4">
            <Form.Label className="text-gray-links mb-3 d-flex align-items-center gap-2">
              {t('project-detail.reschedule-months')}
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip}
              >
                <i className="fa fa-info-circle text-blue-links" style={{ cursor: 'pointer', fontSize: '14px' }}></i>
              </OverlayTrigger>
            </Form.Label>
            <div className="d-flex align-items-center justify-content-center gap-3">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleDecrement}
                disabled={!months || months <= 1}
                style={{ width: '40px', height: '40px', padding: 0 }}
              >
                −
              </button>
              <div
                className="text-center fw-bold text-dark"
                style={{ minWidth: '40px', fontSize: '1.3em' }}
              >
                {months ?? 1}
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleIncrement}
                style={{ width: '40px', height: '40px', padding: 0 }}
              >
                +
              </button>
            </div>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label className="text-gray-links mb-3">
              {t('project-detail.reschedule-direction')}
            </Form.Label>
            <Row>
              <Col>
                <Form.Check
                  type="radio"
                  id="direction-forward"
                  name="direction"
                  label={<span className="text-gray-links">{t('project-detail.reschedule-forward')}</span>}
                  checked={direction === 'forward'}
                  onChange={() => setDirection('forward')}
                />
              </Col>
              <Col>
                <Form.Check
                  type="radio"
                  id="direction-backward"
                  name="direction"
                  label={<span className="text-gray-links">{t('project-detail.reschedule-backward')}</span>}
                  checked={direction === 'backward'}
                  onChange={() => setDirection('backward')}
                />
              </Col>
            </Row>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="text-gray-links mb-3">
              {t('project-detail.reschedule-what-to-shift')}
            </Form.Label>
            <Form.Check
              type="checkbox"
              id="shift-staff"
              label={<span className="text-gray-links">{t('project-detail.reschedule-staff')}</span>}
              checked={shiftStaff}
              onChange={(e) => setShiftStaff(e.target.checked)}
              disabled={isStaffDisabled}
              className="mb-2"
            />
            <Form.Check
              type="checkbox"
              id="shift-external-costs"
              label={<span className="text-gray-links">{t('project-detail.reschedule-external-costs')}</span>}
              checked={shiftExternalCosts}
              onChange={(e) => setShiftExternalCosts(e.target.checked)}
              disabled={isExternalCostsDisabled}
              className="mb-2"
            />
            <Form.Check
              type="checkbox"
              id="shift-progress"
              label={<span className="text-gray-links">{t('project-detail.reschedule-progress')}</span>}
              checked={shiftProgress}
              onChange={(e) => setShiftProgress(e.target.checked)}
              disabled={isRevenueDisabled}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <LinksButton className="btn btn-outline-danger px-4" onClick={handleClose}>
          {t('common.cancel')}
        </LinksButton>
        <LinksButton
          className="btn btn-primary btn-links px-4"
          onClick={handleConfirm}
          isDisabled={!isFormValid()}
        >
          {t('project-detail.reschedule-confirm')}
        </LinksButton>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalReschedule;
